/**
 * Unique ID Extension for TipTap
 * 
 * Adds stable UUIDs to block nodes for reliable addressing by AI agents.
 * 
 * Features:
 * - Auto-generates UUID on node creation
 * - Preserves UUID when node content is edited
 * - Generates NEW UUID on copy/paste to avoid duplicates
 * - Stores ID in node attrs and renders as data-node-id attribute
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";

/**
 * Generate a unique node ID
 */
export function generateNodeId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `node-${timestamp}-${random}`;
}

/**
 * Plugin key for the unique ID plugin
 */
const UniqueIdPluginKey = new PluginKey("uniqueId");

/**
 * Block node types that should have unique IDs
 */
const BLOCK_TYPES = [
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "listItem",
  "blockquote",
  "codeBlock",
  "table",
  "tableRow",
  "tableCell",
  "tableHeader",
];

export interface UniqueIdOptions {
  /**
   * Node types that should have unique IDs
   * @default ["paragraph", "heading", "bulletList", "orderedList", "listItem", "blockquote", "codeBlock", "table"]
   */
  types: string[];
  
  /**
   * Attribute name for the unique ID
   * @default "id"
   */
  attributeName: string;
  
  /**
   * Function to generate unique IDs
   * @default generateNodeId
   */
  generateId: () => string;
}

/**
 * Unique ID Extension
 * 
 * Adds stable UUIDs to block nodes for AI agent addressing.
 */
export const UniqueIdExtension = Extension.create<UniqueIdOptions>({
  name: "uniqueId",

  addOptions() {
    return {
      types: BLOCK_TYPES,
      attributeName: "id",
      generateId: generateNodeId,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          [this.options.attributeName]: {
            default: null,
            parseHTML: (element) => element.getAttribute(`data-node-id`),
            renderHTML: (attributes) => {
              if (!attributes[this.options.attributeName]) {
                return {};
              }
              return {
                "data-node-id": attributes[this.options.attributeName],
              };
            },
          },
          // Patent specification metadata for document structure
          patentSequence: {
            default: null,
            parseHTML: (element) => element.getAttribute(`data-patent-sequence`),
            renderHTML: (attributes) => {
              if (!attributes.patentSequence) {
                return {};
              }
              return {
                "data-patent-sequence": attributes.patentSequence,
              };
            },
          },
          // Patent claims metadata
          claimMetadata: {
            default: null,
            parseHTML: (element) => element.getAttribute(`data-claim-metadata`),
            renderHTML: (attributes) => {
              if (!attributes.claimMetadata) {
                return {};
              }
              return {
                "data-claim-metadata": attributes.claimMetadata,
              };
            },
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    const { types, attributeName, generateId } = this.options;

    return [
      new Plugin({
        key: UniqueIdPluginKey,
        appendTransaction: (transactions, oldState, newState) => {
          // Only process if document changed
          const docChanged = transactions.some((tr) => tr.docChanged);
          if (!docChanged) {
            return null;
          }

          const tr = newState.tr;
          let modified = false;

          // First pass: collect all IDs and their positions to detect duplicates
          const seenIds = new Map<string, number>(); // id -> first occurrence pos
          const nodesToFix: Array<{ pos: number; node: ProseMirrorNode }> = [];

          newState.doc.descendants((node, pos) => {
            if (!types.includes(node.type.name)) {
              return true;
            }

            const existingId = node.attrs[attributeName];

            if (!existingId) {
              // No ID at all — needs a new one
              nodesToFix.push({ pos, node });
            } else if (seenIds.has(existingId)) {
              // Duplicate ID — the second (and later) occurrence gets a new ID
              nodesToFix.push({ pos, node });
            } else {
              // First occurrence of this ID — remember it
              seenIds.set(existingId, pos);
            }

            return true;
          });

          // Second pass: assign new IDs to nodes that need them
          for (const { pos, node } of nodesToFix) {
            const newId = generateId();
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              [attributeName]: newId,
            });
            modified = true;
          }

          if (modified) {
            // Mark as non-user-input so other plugins (e.g. TrackChangesExtension)
            // don't treat node-ID bookkeeping as user edits that should strip marks.
            tr.setMeta("addToHistory", false);
            return tr;
          }
          return null;
        },
      }),
    ];
  },
});

/**
 * Find a node by its unique ID
 * 
 * @param doc - The ProseMirror document
 * @param nodeId - The unique ID to search for
 * @returns The node and its position, or null if not found
 */
export function findNodeById(
  doc: ProseMirrorNode,
  nodeId: string,
  attributeName: string = "id"
): { node: ProseMirrorNode; pos: number } | null {
  let result: { node: ProseMirrorNode; pos: number } | null = null;

  doc.descendants((node, pos) => {
    if (result) return false; // Stop if already found
    
    if (node.attrs[attributeName] === nodeId) {
      result = { node, pos };
      return false; // Stop traversal
    }
    
    return true; // Continue traversal
  });

  return result;
}

/**
 * Get all nodes with their IDs
 * 
 * @param doc - The ProseMirror document
 * @returns Array of nodes with their IDs and positions
 */
export function getAllNodesWithIds(
  doc: ProseMirrorNode,
  attributeName: string = "id"
): Array<{ node: ProseMirrorNode; pos: number; id: string }> {
  const nodes: Array<{ node: ProseMirrorNode; pos: number; id: string }> = [];

  doc.descendants((node, pos) => {
    const id = node.attrs[attributeName];
    if (id) {
      nodes.push({ node, pos, id });
    }
    return true;
  });

  return nodes;
}

export default UniqueIdExtension;
